import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  mergeOverrides, renderOverridesCss,
  type OverrideChange, type OverrideProp, type OverrideRule, type OverrideViewport,
} from '@evo-hub/shared';
import { useToast } from '../../ui/Toast.js';
import {
  useComments, useCreateComment, useDeleteComment, useApplyComments,
  type PageComment,
} from '../../../hooks/useProjects.js';
import { useRegionDraw } from './useRegionDraw.js';
import { useIframeSelection } from './useIframeSelection.js';
import { useInlineTextEdit } from './useInlineTextEdit.js';
import {
  useApplyEdits, useEditorContext, useUploadProjectImage, type ManualEditOp,
} from '../../../hooks/useProjects.js';
import { TEXT_TAGS } from './textEditLedger.js';
import { inspectorState, classChangeOps, type GroupState } from './inspector.js';
import type { PendingChange } from './sessionHistory.js';
import type { EditabilityBlock } from './textEditLedger.js';
import { resolveEditTarget, contenedorDelIcono, type Escalon } from './editTarget.js';

// ── Estado del editor visual, compartido entre la vista previa y el panel ─────
//
// Vive acá y no dentro de PagePreview porque los controles no están sobre la
// vista previa: están en el panel de Evo Brain, a la derecha, que es otro punto
// del árbol. El elemento del panel se crea en la página del proyecto y lo
// renderiza el dock, así que el estado se pasa por props, no por contexto —
// un contexto se resolvería desde donde el dock está montado, no desde acá.
//
// Cuando lleguen el inspector de propiedades y los comentarios, van en este
// mismo estado y en el mismo panel: por eso `mode` es una unión abierta y no un
// booleano.

/**
 * 'view'    — mirar, sin tocar.
 * 'edit'    — tocar la página directo, sin modelo.
 * 'comment' — marcar y pedir un cambio; lo resuelve el modelo.
 */
export type PageEditorMode = 'view' | 'edit' | 'comment';

/**
 * Ancho con el que se muestra la página. NO cambia cómo se guarda: los templates
 * traen su propio CSS responsive, así que esto es mirar, no editar por breakpoint.
 */
export type PageViewport = 'desktop' | 'tablet' | 'mobile';

export const VIEWPORT_WIDTH: Record<PageViewport, string> = {
  desktop: '100%',
  tablet: '768px',
  mobile: '390px',
};

/** Un escalón del camino de ancestros, para poder subir sin puntería. */
export interface AncestorStep {
  targetId: string;
  tag: string;
}

/** Por qué la página no se puede editar, cuando no se puede. */
export type PageEditorBlocker =
  /** Se está mirando una versión anterior a la actual. */
  | 'viewing-old'
  /**
   * La página no tiene data-evo-id: se generó antes de que existiera el editor,
   * o la restauró una revisión aprobada. Sin ids no hay nada que seleccionar.
   */
  | 'not-stamped';

/** Qué está seleccionado y si se puede tocar. Lo muestra el panel. */
export interface PageEditorSelection {
  tag: string;
  /**
   * Si lo seleccionado es una imagen de la página, su filename. Es lo que
   * permite reemplazarla desde la imagen misma en vez de ir a buscarla al cajón
   * de imágenes pendientes.
   */
  imageFilename?: string;
  editable: boolean;
  /** Solo si no es editable: por qué. */
  reason?: EditabilityBlock;
  /** Estado de cada propiedad del inspector para este elemento. */
  groups: GroupState[];
  /** true si el texto de este elemento se editó a mano alguna vez. */
  textoEditadoAMano: boolean;

  /** El id del elemento, para las acciones que no pasan por el DOM. */
  targetId: string;
  /** Destino actual, solo si lo seleccionado es un enlace. */
  href?: string;
  /** true si el destino se cambió a mano alguna vez. */
  destinoEditadoAMano: boolean;
  /**
   * A qué se puede convertir este texto. Vacío si no es un texto convertible.
   * Si el template no estila encabezados por etiqueta, `sinEstilosDeEncabezado`
   * queda en true y el panel lo dice en vez de ofrecer un cambio que no se ve.
   */
  tagOptions: string[];
  sinEstilosDeEncabezado: boolean;
  /** true si la jerarquía se cambió a mano alguna vez. */
  etiquetaEditadaAMano: boolean;
  /** ¿Se puede mover el bloque hacia ese lado? */
  puedeSubir: boolean;
  puedeBajar: boolean;
}

export interface PageEditorState {
  mode: PageEditorMode;
  setMode: (m: PageEditorMode) => void;
  /** null = se puede editar. */
  blocker: PageEditorBlocker | null;
  iframeRef: React.MutableRefObject<HTMLIFrameElement | null>;
  pendingCount: number;
  /** Qué página se está editando, relativa a la carpeta de la versión. */
  activeFile: string;
  /**
   * Cambia la página en edición. Devuelve false —y no cambia nada— si hay
   * ediciones sin guardar: se aplicarían sobre el archivo equivocado.
   */
  setActiveFile: (file: string) => boolean;
  isSaving: boolean;
  save: () => void;
  discard: () => void;
  /** Elemento seleccionado ahora mismo, o null. */
  selection: PageEditorSelection | null;
  /** Camino desde la raíz hasta lo seleccionado. El último es lo seleccionado. */
  ancestors: AncestorStep[];
  /** Comentarios guardados en esta versión. */
  comments: PageComment[];
  /** Lo marcado y todavía sin escribir. */
  draft: CommentDraft | null;
  /** true mientras se arrastra una zona. */
  dibujandoZona: boolean;
  addComment: (texto: string) => void;
  removeComment: (commentId: string) => void;
  applyAllComments: () => void;
  isApplyingComments: boolean;
  /** Cambia el destino del enlace seleccionado. */
  setLinkHref: (value: string) => void;
  /** Convierte el texto seleccionado en párrafo o encabezado. */
  setTextTag: (value: string) => void;
  /** Duplica el bloque seleccionado, y queda seleccionada la copia. */
  duplicateBlock: () => void;
  /** Borra el bloque seleccionado. */
  removeBlock: () => void;
  /** Sube o baja el bloque seleccionado. */
  moveBlock: (direction: 'up' | 'down') => void;
  /** Selecciona un ancestro por su id. */
  selectAncestor: (targetId: string) => void;
  viewport: PageViewport;
  setViewport: (v: PageViewport) => void;
  /** Cuántos textos de la página se pueden editar. null = todavía sin mirar. */
  editableCount: number | null;
  /** Cambia una utility del grupo en el elemento seleccionado. */
  setGroupValue: (group: GroupState, next: string | null) => void;
  /** Valor de un ajuste sobre el elemento seleccionado, en una vista. */
  styleValue: (viewport: OverrideViewport, prop: OverrideProp) => string | null;
  /** Pone o borra (null) un ajuste. `viewport: 'all'` = en todas las vistas. */
  setStyleValue: (viewport: OverrideViewport, prop: OverrideProp, next: string | null) => void;
  applyFormat: (accion: 'bold' | 'lineBreak' | 'bullets') => void;
  canFormat: boolean;
  formatActions: Array<'bold' | 'lineBreak' | 'bullets'>;
  /** Lista de cambios sin guardar, para poder revisarlos y revertir uno. */
  pendingChanges: PendingChange[];
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  revert: (cambio: PendingChange) => void;
  /** Reemplaza la imagen seleccionada. Sube el archivo y recarga la vista. */
  replaceImage: (file: File) => void;
  isUploadingImage: boolean;
  /** Vuelve a mirar el documento del iframe (después de recargarlo). */
  refreshStampStatus: () => void;
}

export interface UsePageEditorStateOpts {
  projectId: number;
  /** true si se está viendo una versión anterior a la actual. */
  isViewingOld: boolean;
  /** Cambia cuando el iframe carga otra versión: fuerza revisar los ids. */
  previewKey: string;
  /** Avisa que se creó una versión nueva (al aplicar comentarios). */
  onVersionChange?: (version: number) => void;
}

/** Lo marcado y todavía sin escribir: un elemento, o una zona rayada. */
export interface CommentDraft {
  kind: 'element' | 'region';
  targetIds: string[];
  tag?: string;
  rect?: { top: number; left: number; width: number; height: number };
  viewportWidth?: number;
}

/**
 * Filename de la imagen que representa este elemento, si lo es.
 *
 * Sirven las dos formas en que puede estar puesta: como placeholder todavía sin
 * subir (/placeholder?...label=x.jpg) o como imagen ya subida (img/x.jpg).
 */
function filenameDeSrc(src: string): string | null {
  const ph = /[?&]label=([^&]+)/.exec(src);
  if (ph?.[1]) return decodeURIComponent(ph[1]);
  if (src.startsWith('img/')) return src.slice('img/'.length).split(/[?#]/)[0] ?? null;
  return null;
}

/** Imágenes del proyecto adentro de un elemento (no assets del template). */
function imagenesDelProyecto(root: Element): HTMLImageElement[] {
  return Array.from(root.querySelectorAll('img'))
    .filter(i => filenameDeSrc(i.getAttribute('src') ?? '') !== null);
}

function filenameDeImagen(el: HTMLElement): string | null {
  if (el.tagName.toLowerCase() === 'img') {
    return filenameDeSrc(el.getAttribute('src') ?? '');
  }

  // Un placeholder sin subir dibuja un <svg> ENCIMA de la imagen, así que el clic
  // cae en el ícono y no en el <img>. Se sube por el árbol hasta encontrar un
  // contenedor con UNA sola imagen del proyecto: si hay una sola, no hay
  // ambigüedad sobre cuál se quiso tocar. Es el mismo criterio que la
  // redirección del clic en contenedores.
  let actual: Element | null = el;
  for (let i = 0; i < 3 && actual; i++) {
    const imgs = imagenesDelProyecto(actual);
    if (imgs.length === 1) return filenameDeSrc(imgs[0]!.getAttribute('src') ?? '');
    if (imgs.length > 1) return null;
    actual = actual.parentElement;
  }
  return null;
}

export function usePageEditorState(opts: UsePageEditorStateOpts): PageEditorState {
  const { projectId, isViewingOld, previewKey, onVersionChange } = opts;
  // La página en edición. Vive acá y no en la pantalla porque es lo que
  // decide a QUÉ archivo van las ediciones, los comentarios y las
  // iteraciones: tenerla en dos lados es tenerla mal en uno.
  const [activeFile, setActiveFileState] = useState('index.html');
  const { t } = useTranslation();
  const toast = useToast();

  const [mode, setModeState] = useState<PageEditorMode>('view');
  // handleSelect se registra una vez y lee el modo de acá: el estado de React
  // llega un render tarde y un clic justo al cambiar de modo abriría la edición.
  const modeRef = useRef<PageEditorMode>('view');
  useEffect(() => { modeRef.current = mode; }, [mode]);
  const [isStamped, setIsStamped] = useState<boolean | null>(null);
  const [editableCount, setEditableCount] = useState<number | null>(null);
  const [selection, setSelection] = useState<PageEditorSelection | null>(null);
  const [ancestors, setAncestors] = useState<AncestorStep[]>([]);
  const [viewport, setViewport] = useState<PageViewport>('desktop');

  const applyEdits = useApplyEdits(projectId);
  const uploadImage = useUploadProjectImage(projectId);
  // Qué puede expresar el template y qué se tocó a mano. Solo se pide en Editar.
  const { data: ctx } = useEditorContext(projectId || undefined, mode === 'edit');
  const { data: commentsData } = useComments(projectId || undefined, mode === 'comment');
  const createComment = useCreateComment(projectId);
  const deleteComment = useDeleteComment(projectId);
  const applyComments = useApplyComments(projectId);

  /** Clases que una edición manual puso en ESTE elemento y siguen puestas. */
  const clasesManuales = useCallback((targetId: string, actuales: string[]): Set<string> => {
    const puestas = new Set<string>();
    for (const e of ctx?.manualEdits ?? []) {
      if (e.targetId !== targetId || e.type !== 'class') continue;
      for (const c of e.removed ?? []) puestas.delete(c);
      for (const c of e.added ?? []) puestas.add(c);
    }
    const presentes = new Set(actuales);
    for (const c of [...puestas]) if (!presentes.has(c)) puestas.delete(c);
    return puestas;
  }, [ctx]);

  /** El elemento seleccionado, para poder releer sus clases al cambiarlas. */
  const seleccionadoRef = useRef<HTMLElement | null>(null);

  const getDoc = useCallback(
    () => iframeRef.current?.contentWindow?.document ?? null,
    // iframeRef lo devuelve el hook de abajo; la referencia es estable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const inlineEdit = useInlineTextEdit(getDoc);

  const describir = useCallback((el: HTMLElement): PageEditorSelection => {
    const clase = inlineEdit.classify(el);
    const targetId = el.getAttribute('data-evo-id') ?? '';
    const clases = Array.from(el.classList);
    const grupos = inspectorState(ctx?.groups ?? [], clases, clasesManuales(targetId, clases));
    const tocado = (tipo: string): boolean =>
      (ctx?.manualEdits ?? []).some(e => e.targetId === targetId && e.type === tipo);
    const tag = el.tagName.toLowerCase();

    // Convertir es solo entre etiquetas de texto: cambiar la jerarquía de un
    // párrafo, no convertir un contenedor en otra cosa.
    const encabezados = ctx?.headingTags ?? [];
    const convertible = TEXT_TAGS.includes(tag);
    const opcionesDeTag = convertible && encabezados.length > 0
      ? ['p', ...encabezados]
      : [];

    return {
      tag,
      targetId,
      ...(() => { const f = filenameDeImagen(el); return f ? { imageFilename: f } : {}; })(),
      editable: clase.kind !== 'none',
      ...(clase.kind === 'none' ? { reason: clase.reason } : {}),
      groups: grupos,
      textoEditadoAMano: tocado('text'),
      ...(tag === 'a' ? { href: el.getAttribute('href') ?? '' } : {}),
      destinoEditadoAMano: tocado('href'),
      tagOptions: opcionesDeTag,
      // El template no tiene estilos de encabezado por etiqueta: convertir no se
      // vería. Se dice, igual que un grupo de utilities que no existe.
      sinEstilosDeEncabezado: convertible && encabezados.length === 0,
      etiquetaEditadaAMano: tocado('tag'),
      puedeSubir: inlineEdit.puedeMover(targetId, 'up'),
      puedeBajar: inlineEdit.puedeMover(targetId, 'down'),
    };
  }, [inlineEdit, ctx, clasesManuales]);

  const handleSelect = useCallback((el: HTMLElement | null) => {
    if (!el) { seleccionadoRef.current = null; setSelection(null); return; }
    seleccionadoRef.current = el;
    // Se publica SIEMPRE qué se seleccionó, se pueda editar o no: el panel tiene
    // que poder decir por qué ese elemento no se toca.
    setSelection(describir(el));

    // Camino de ancestros direccionables, de afuera hacia adentro. Es lo que
    // permite subir a la card cuando se hizo clic en su título, sin tener que
    // acertarle al padding.
    const camino: AncestorStep[] = [];
    let actual: Element | null = el;
    while (actual && camino.length < 6) {
      const id = actual.getAttribute('data-evo-id');
      if (id) camino.unshift({ targetId: id, tag: actual.tagName.toLowerCase() });
      actual = actual.parentElement;
    }
    setAncestors(camino);
    // Seleccionar y editar son el mismo gesto EN MODO EDITAR. Comentando, marcar
    // es solo marcar: no se va a escribir nada sobre la página.
    if (modeRef.current !== 'comment') inlineEdit.beginEdit(el, { placeCaret: false });
  }, [inlineEdit, describir]);

  /**
   * Cambiar una utility se aplica YA sobre el DOM del iframe, para que el efecto
   * se vea al instante, y se registra como op para el guardado. Es el mismo
   * criterio que el texto: se ve, y recién al guardar se escribe en el archivo.
   */
  // ── Ajustes por vista ──────────────────────────────────────────────────────
  //
  // Lo guardado viene del servidor; lo que todavía no se guardó sale de los
  // cambios pendientes. La hoja de preview se arma con la suma de los dos, así
  // que deshacer y rehacer se reflejan solos: cambia la lista, se repinta.
  const reglasVigentes = useMemo((): OverrideRule[] => {
    const cambios: OverrideChange[] = [];
    for (const c of inlineEdit.edits) {
      if (c.type !== 'style' || !c.viewport || !c.prop) continue;
      cambios.push({ targetId: c.targetId, viewport: c.viewport, prop: c.prop, value: c.value ?? null });
    }
    return mergeOverrides(ctx?.overrides ?? [], cambios);
  }, [ctx?.overrides, inlineEdit.edits]);

  /** Valor puesto hoy sobre el elemento seleccionado, en una vista concreta. */
  const styleValue = useCallback((viewport: OverrideViewport, prop: OverrideProp): string | null => {
    const targetId = seleccionadoRef.current?.getAttribute('data-evo-id');
    if (!targetId) return null;
    return reglasVigentes.find(
      r => r.targetId === targetId && r.viewport === viewport && r.prop === prop,
    )?.value ?? null;
  }, [reglasVigentes]);

  const setStyleValue = useCallback((
    viewport: OverrideViewport,
    prop: OverrideProp,
    next: string | null,
  ) => {
    const el = seleccionadoRef.current;
    const targetId = el?.getAttribute('data-evo-id');
    if (!targetId) return;
    inlineEdit.registrarEstilo(targetId, viewport, prop, styleValue(viewport, prop), next);
    if (el) setSelection(describir(el));
  }, [inlineEdit, styleValue, describir]);

  // La hoja de preview vive DENTRO del iframe: es el único lugar donde el
  // navegador la va a aplicar a la página. Se reescribe entera en cada cambio,
  // igual que la del servidor — un parche dejaría reglas que ya se borraron.
  useEffect(() => {
    const doc = getDoc();
    if (!doc) return;
    const ID = 'evo-preview-overrides';
    let tag = doc.getElementById(ID) as HTMLStyleElement | null;
    if (!tag) {
      tag = doc.createElement('style');
      tag.id = ID;
      doc.head.appendChild(tag);
    }
    tag.textContent = renderOverridesCss(reglasVigentes);
  }, [reglasVigentes, getDoc, previewKey]);

  const setGroupValue = useCallback((group: GroupState, next: string | null) => {
    const el = seleccionadoRef.current;
    if (!el) return;
    const targetId = el.getAttribute('data-evo-id');
    if (!targetId) return;
    const { add, remove } = classChangeOps(group, next);
    if (add.length === 0 && remove.length === 0) return;

    for (const c of remove) el.classList.remove(c);
    for (const c of add) el.classList.add(c);
    inlineEdit.registrarClases(targetId, add, remove);
    setSelection(describir(el));
  }, [describir, inlineEdit]);

  const puedeEditar = useCallback(
    (el: HTMLElement) => inlineEdit.classify(el).kind !== 'none',
    [inlineEdit],
  );

  // Pegarle al padding de una card seleccionaba la card. Si adentro hay un solo
  // texto editable, el clic va ahí: no hay otra cosa que se pueda haber querido
  // tocar. Se aplica también al hover, así que la marca cae sobre el texto.
  const mapTarget = useCallback((el: HTMLElement, point?: { x: number; y: number }): HTMLElement => {
    // Un placeholder de imagen dibuja un ícono ENCIMA. El clic cae en el ícono y
    // el panel decía "esto no tiene texto" sobre la imagen que se quería cambiar.
    // Con el punto se puede mirar qué más hay debajo del cursor.
    if (point && !puedeEditar(el)) {
      const doc = getDoc();
      const debajo = doc?.elementsFromPoint(point.x, point.y) ?? [];
      const imagen = debajo.find(
        (e): e is HTMLImageElement =>
          e.tagName.toLowerCase() === 'img' &&
          filenameDeSrc(e.getAttribute('src') ?? '') !== null,
      );
      if (imagen) return imagen;
    }

    const destino = resolveEditTarget(el, {
      esEditable: e => inlineEdit.classify(e as HTMLElement).kind !== 'none',
      esContenedor: e => {
        const c = inlineEdit.classify(e as HTMLElement);
        return c.kind === 'none' && c.reason === 'container';
      },
    });
    return destino as HTMLElement;
  }, [inlineEdit]);

  /**
   * Arranca la edición desde el mousedown, sin tocar el cursor: el navegador lo
   * coloca donde se hizo clic. La selección del panel se actualiza igual, porque
   * el click posterior sigue llegando a onSelect.
   */
  const promover = useCallback((el: HTMLElement) => {
    inlineEdit.beginEdit(el, { placeCaret: false });
  }, [inlineEdit, puedeEditar, getDoc]);

  const enComentario = mode === 'comment';

  /**
   * Comentando, el clic prefiere el contenedor cuando el hijo lo llena entero.
   *
   * El círculo con la flecha de un botón es un <span> con su fondo y su borde,
   * y adentro un <svg> que lo llena: clickearlo marcaba el svg, y para tocar el
   * margen del círculo había que subir un escalón a mano. Ver contenedorQueLlena.
   *
   * Solo comentando. Editando, la redirección va al revés —del contenedor al
   * texto de adentro— porque ahí lo que se quiere es escribir.
   */
  const preferirContenedor = useCallback((el: HTMLElement): HTMLElement => {
    const cadena: Escalon[] = [];
    const nodos: HTMLElement[] = [];
    // Solo hacen falta el elemento y su contenedor: se sube un escalón como
    // máximo (ver contenedorDelIcono).
    for (let n: HTMLElement | null = el; n && nodos.length < 2; n = n.parentElement) {
      const id = n.getAttribute('data-evo-id');
      if (!id) continue;
      nodos.push(n);
      // Texto PROPIO: el del contenedor incluiría el del hijo y nunca daría vacío.
      let propio = '';
      for (const c of Array.from(n.childNodes)) {
        if (c.nodeType === 3) propio += c.textContent ?? '';
      }
      cadena.push({
        targetId: id,
        tag: n.tagName.toLowerCase(),
        texto: propio.trim(),
        hijosElemento: n.children.length,
      });
    }
    return nodos[contenedorDelIcono(cadena)] ?? el;
  }, []);

  const { iframeRef, clearSelection, selectElement } = useIframeSelection({
    enabled: (mode === 'edit' || enComentario) && !isViewingOld && isStamped === true,
    onSelect: handleSelect,
    // Comentando se puede marcar CUALQUIER cosa que tenga identificador: una
    // sección de tres columnas es el caso de uso, no una excepción. El filtro de
    // editable es de la otra herramienta — acá no se va a escribir nada.
    ...(enComentario
      ? { mapTarget: preferirContenedor }
      : { isEditable: puedeEditar, mapTarget, onPromote: promover }),
    onEscape: () => { inlineEdit.commitEdit(); clearSelection(); },
  });

  /**
   * ¿La página servida tiene data-evo-id?
   *
   * Se mira el documento real del iframe, no el estado del proyecto: una página
   * puede tener versión actual y aun así venir sin ids (generada antes del
   * editor, o restaurada desde una revisión aprobada). Sin esto el modo Editar
   * se abre y no responde a ningún clic, sin decir por qué.
   */
  const refreshStampStatus = useCallback(() => {
    const doc = iframeRef.current?.contentWindow?.document;
    if (!doc?.body) { setIsStamped(null); setEditableCount(null); return; }
    const marcados = Array.from(doc.querySelectorAll('[data-evo-id]'));
    setIsStamped(marcados.length > 0);
    // Cuántos textos se pueden editar de verdad. Es el número que dice si el
    // editor sirve en esta página, y el que delata si algo no está funcionando.
    setEditableCount(marcados.filter(el => puedeEditar(el as HTMLElement)).length);
  }, [iframeRef, puedeEditar]);

  // Al cambiar de versión el documento se reemplaza: se olvida lo que se sabía y
  // se vuelve a mirar cuando la vista previa avise que cargó.
  //
  // Quien avisa es PagePreview (onLoad del iframe) y no un listener puesto desde
  // acá: este hook corre en la página del proyecto, que puede renderizar antes
  // de que el iframe exista — con el listener puesto desde acá, entrar por otra
  // pestaña y después ir a Diseño dejaba la detección sin correr nunca, y el
  // editor quedaba mudo: ni marcaba ni explicaba.
  useEffect(() => {
    setIsStamped(null);
    setEditableCount(null);
    setSelection(null);
  }, [previewKey]);

  const blocker: PageEditorBlocker | null =
    isViewingOld ? 'viewing-old'
    : isStamped === false ? 'not-stamped'
    : null;

  const setMode = useCallback((next: PageEditorMode) => {
    setModeState(prev => {
      if (next === prev) return prev;
      // Salir de Editar con cambios sin guardar los perdería en silencio.
      if (next === 'view' && inlineEdit.getEdits().length > 0) inlineEdit.discardAll();
      inlineEdit.commitEdit();
      clearSelection();
      return next;
    });
  }, [inlineEdit, clearSelection]);

  // Mirar una versión vieja saca del modo Editar: no se edita historia.
  useEffect(() => {
    if (isViewingOld && mode === 'edit') setMode('view');
  }, [isViewingOld, mode, setMode]);

  const save = useCallback(() => {
    void (async () => {
      // commitEdit cierra la edición en curso; getEdits lee del registro, así
      // que incluye ese último cambio (el estado de React todavía no).
      inlineEdit.commitEdit();
      // 'own' solo cambia el texto propio y deja los hijos (íconos, chevrons)
      // intactos; 'full' reemplaza todo el contenido del elemento.
      // Las ops salen del historial consolidado: es la MISMA fuente que lista el
      // panel de cambios pendientes, así que no pueden discrepar.
      const ops: ManualEditOp[] = inlineEdit.getEdits().flatMap((c): ManualEditOp[] => {
        if (c.type === 'text') {
          if (c.value === undefined) return [];
          return [{
            op: c.kind === 'own' ? 'setOwnText' : 'setRichText',
            targetId: c.targetId,
            value: c.value,
          }];
        }
        if (c.type === 'href') {
          return c.value === undefined ? [] : [{ op: 'setHref', targetId: c.targetId, value: c.value }];
        }
        if (c.type === 'tag') {
          return c.value === undefined ? [] : [{ op: 'setTag', targetId: c.targetId, value: c.value }];
        }
        if (c.type === 'structure') {
          if (c.action === 'duplicate') {
            return c.idMap ? [{ op: 'duplicateNode', targetId: c.targetId, idMap: c.idMap }] : [];
          }
          if (c.action === 'remove') return [{ op: 'removeNode', targetId: c.targetId }];
          return c.direction
            ? [{ op: 'moveNode', targetId: c.targetId, direction: c.direction }]
            : [];
        }
        if (c.type === 'style') {
          if (!c.viewport || !c.prop) return [];
          // `undefined` acá es "sin ajuste": se manda null, que en el servidor
          // borra la regla en vez de escribir un valor vacío.
          return [{
            op: 'setStyle',
            targetId: c.targetId,
            viewport: c.viewport,
            prop: c.prop,
            value: c.value ?? null,
          }];
        }
        return [{
          op: 'setClasses',
          targetId: c.targetId,
          add: c.added ?? [],
          remove: c.removed ?? [],
        }];
      });
      if (ops.length === 0) return;

      try {
        const result = await applyEdits.mutateAsync({ ops, file: activeFile });
        inlineEdit.clearEdits();
        clearSelection();
        setModeState('view');
        if (result.failed > 0) {
          toast.error(t('projects.editor.savedPartial', {
            applied: result.applied, failed: result.failed,
          }));
        } else {
          toast.success(t('projects.editor.saved', { version: result.version }));
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : t('common.error'));
      }
    })();
  }, [inlineEdit, applyEdits, clearSelection, toast, t]);

  /**
   * Reemplazar la imagen NO pasa por el guardado de ediciones: se sube al
   * directorio de la versión actual y la vista se recarga. Es el mismo camino
   * que el cajón de imágenes pendientes, disparado desde la imagen.
   */
  const replaceImage = useCallback((file: File) => {
    const filename = selection?.imageFilename;
    if (!filename) return;
    void (async () => {
      try {
        await uploadImage.mutateAsync({ file, filename });
        // Recargar la vista para que aparezca la imagen nueva. Se usa
        // contentWindow.location.reload() y no reasignar src: asignarle su propio
        // valor no siempre recarga, y además el linter lo lee como un no-op.
        iframeRef.current?.contentWindow?.location.reload();
        toast.success(t('projects.editor.image.replaced', { filename }));
      } catch (err) {
        toast.error(err instanceof Error ? err.message : t('common.error'));
      }
    })();
  }, [selection, uploadImage, iframeRef, toast, t]);

  /** Cambia el destino del enlace seleccionado. El valor ya viene normalizado. */
  const setLinkHref = useCallback((value: string) => {
    const targetId = selection?.targetId;
    if (!targetId) return;
    inlineEdit.setHref(targetId, value);
    const el = getDoc()?.querySelector(`[data-evo-id="${targetId}"]`);
    if (el) setSelection(describir(el as HTMLElement));
  }, [selection, inlineEdit, getDoc, describir]);

  /** Convierte el texto seleccionado en párrafo o encabezado. */
  const setTextTag = useCallback((value: string) => {
    const targetId = selection?.targetId;
    if (!targetId) return;
    const nuevo = inlineEdit.setTag(targetId, value);
    // El elemento se reemplazó: la selección tiene que apuntar al nodo nuevo, no
    // al que quedó fuera del documento.
    if (nuevo) { selectElement(nuevo); setSelection(describir(nuevo)); }
  }, [selection, inlineEdit, selectElement, describir]);

  /** Duplica el bloque seleccionado y deja seleccionada la copia. */
  const duplicateBlock = useCallback(() => {
    const targetId = selection?.targetId;
    if (!targetId) return;
    const copiaId = inlineEdit.duplicateBlock(targetId);
    if (!copiaId) return;
    const copia = getDoc()?.querySelector(`[data-evo-id="${copiaId}"]`);
    if (copia) { selectElement(copia as HTMLElement); setSelection(describir(copia as HTMLElement)); }
  }, [selection, inlineEdit, getDoc, selectElement, describir]);

  /** Borra el bloque seleccionado. Se puede deshacer. */
  const removeBlock = useCallback(() => {
    const targetId = selection?.targetId;
    if (!targetId) return;
    if (!inlineEdit.removeBlock(targetId)) return;
    clearSelection();
    setSelection(null);
    setAncestors([]);
  }, [selection, inlineEdit, clearSelection]);

  /** Sube o baja el bloque seleccionado entre sus hermanos. */
  const moveBlock = useCallback((direction: 'up' | 'down') => {
    const targetId = selection?.targetId;
    if (!targetId) return;
    if (!inlineEdit.moveBlock(targetId, direction)) return;
    const el = getDoc()?.querySelector(`[data-evo-id="${targetId}"]`);
    if (el) {
      // Se vuelve a describir porque cambió si tiene hermano arriba o abajo, y
      // se redibuja la marca, que quedó donde estaba el bloque antes.
      selectElement(el as HTMLElement);
      setSelection(describir(el as HTMLElement));
    }
  }, [selection, inlineEdit, getDoc, selectElement, describir]);

  // ── Comentarios ───────────────────────────────────────────────────────────
  //
  // El borrador es lo que está marcado y todavía no se escribió: un elemento
  // (por clic) o una zona (por arrastre). Se limpia al guardar el comentario.
  const [draft, setDraft] = useState<CommentDraft | null>(null);
  /** Cuándo se terminó de rayar. Ver onRegion. */
  const rayadoReciente = useRef(0);

  const { dibujando } = useRegionDraw({
    enabled: mode === 'comment' && !isViewingOld && isStamped === true,
    iframeRef,
    onRegion: r => {
      clearSelection();
      setDraft({ kind: 'region', targetIds: r.targetIds, rect: r.rect, viewportWidth: r.viewportWidth });
      // Soltar el arrastre dispara además un 'click', que selecciona el elemento
      // de abajo y pisaba el borrador de zona con uno de elemento. La marca dura
      // lo que tarda ese click en llegar; un clic de verdad viene después y sí
      // tiene que reemplazar la zona.
      rayadoReciente.current = Date.now();
    },
  });

  // Un clic sobre un elemento, comentando, arma el otro tipo de borrador.
  useEffect(() => {
    if (mode !== 'comment') { setDraft(null); return; }
    if (!selection?.targetId) return;
    if (Date.now() - rayadoReciente.current < 600) return;
    setDraft({
      kind: 'element',
      targetIds: [selection.targetId],
      tag: selection.tag,
    });
  }, [mode, selection?.targetId, selection?.tag]);

  const addComment = useCallback((texto: string) => {
    if (!draft) return;
    void (async () => {
      try {
        const r = await createComment.mutateAsync({
          kind: draft.kind,
          targetIds: draft.targetIds,
          text: texto,
          ...(draft.rect ? { rect: draft.rect } : {}),
          ...(draft.viewportWidth ? { viewportWidth: draft.viewportWidth } : {}),
          file: activeFile,
        });
        // Una zona sin captura le dice bastante menos al modelo: se avisa en vez
        // de guardarla muda y que el resultado sorprenda.
        if (r.captureError) toast.error(t('projects.comments.captureFailed'));
        setDraft(null);
        clearSelection();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : t('common.error'));
      }
    })();
  }, [draft, createComment, clearSelection, toast, t]);

  const removeComment = useCallback((commentId: string) => {
    void deleteComment.mutateAsync(commentId).catch((err: unknown) => {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    });
  }, [deleteComment, toast, t]);

  const applyAllComments = useCallback(() => {
    void (async () => {
      try {
        const r = await applyComments.mutateAsync(activeFile);

        // El motivo, no dónde buscarlo. Con "abajo dice por qué" Eduardo
        // reintentó a los 25 segundos: nada en el mensaje le decía que repetir
        // no iba a cambiar nada.
        const motivos = r.comments
          .filter(c => c.status === 'pending' && c.note)
          .map(c => c.note ?? '')
          .join(' · ');

        if (r.applied === 0) {
          // El sistema contestó bien: preguntó algo que no se pudo hacer y dijo
          // por qué. No es un error, y pintarlo de rojo hace que lo parezca.
          toast.info(motivos
            ? t('projects.comments.noneAppliedWhy', { reason: motivos })
            : t('projects.comments.noneApplied'));
        } else if (r.pending > 0) {
          toast.warning(t('projects.comments.appliedPartial', {
            applied: r.applied, pending: r.pending, reason: motivos,
          }));
        } else {
          toast.success(t('projects.comments.applied', { count: r.applied, version: r.version }));
        }
        // Lo que se escribió fuera del sistema de diseño va aparte y siempre:
        // es lo que no se puede descubrir mirando la página.
        if (r.systemEscapes) toast.warning(t('projects.comments.escaped', { detail: r.systemEscapes }));
        // El cambio se ve; el desvío no. Si tocó algo que no estaba marcado, se
        // dice — sin esto un comentario resuelto en el elemento equivocado se
        // ve igual que uno resuelto bien.
        if (r.outsideAnchor) toast.warning(t('projects.comments.outsideAnchor', { detail: r.outsideAnchor }));
        // Sin cambios no hay versión nueva: no hay adónde saltar.
        if (r.sinCambios === true) return;
        onVersionChange?.(r.version);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : t('common.error'));
      }
    })();
  }, [applyComments, toast, t, onVersionChange]);

  const selectAncestor = useCallback((targetId: string) => {
    const doc = getDoc();
    // `instanceof HTMLElement` NO sirve acá: el elemento vive en el documento
    // del iframe, que tiene su propia jerarquía de clases. Comparado contra el
    // HTMLElement de esta ventana da false SIEMPRE, y el breadcrumb se dibujaba
    // pero no seleccionaba nada. La comprobación va contra la vista del propio
    // documento, que es la que define esas clases.
    const el = doc?.querySelector(`[data-evo-id="${targetId}"]`) as HTMLElement | null;
    if (!el) return;
    // Se cierra la edición abierta antes de mover la selección: si no, el
    // elemento anterior sigue en contenteditable y el foco pelea con la
    // selección nueva.
    inlineEdit.commitEdit();
    selectElement(el);
    handleSelect(el);
  }, [getDoc, selectElement, handleSelect, inlineEdit]);

  const discard = useCallback(() => {
    // discardAll revierte todo lo del historial, texto y clases: son el mismo
    // registro, así que no hay dos listas que puedan quedar desfasadas.
    inlineEdit.discardAll();
    clearSelection();
    setSelection(null);
  }, [inlineEdit, clearSelection]);

  return {
    mode,
    setMode,
    blocker,
    iframeRef,
    pendingCount: inlineEdit.edits.length,
    activeFile,
    setActiveFile: (file: string): boolean => {
      // Las ediciones pendientes están ancladas a ids del HTML de ESTA página.
      // Guardarlas después de cambiar las escribiría en el archivo equivocado,
      // así que el cambio se frena y quien edita decide: guardar o descartar.
      if (inlineEdit.edits.length > 0) return false;
      setActiveFileState(file);
      return true;
    },
    isSaving: applyEdits.isPending,
    save,
    discard,
    selection,
    editableCount,
    setGroupValue,
    applyFormat: inlineEdit.applyFormat,
    canFormat: inlineEdit.canFormat,
    formatActions: inlineEdit.formatActions,
    pendingChanges: inlineEdit.edits,
    styleValue,
    setStyleValue,
    undo: inlineEdit.undo,
    redo: inlineEdit.redo,
    canUndo: inlineEdit.canUndo,
    canRedo: inlineEdit.canRedo,
    revert: inlineEdit.revert,
    replaceImage,
    isUploadingImage: uploadImage.isPending,
    ancestors,
    selectAncestor,
    comments: commentsData?.comments ?? [],
    draft,
    dibujandoZona: dibujando,
    addComment,
    removeComment,
    applyAllComments,
    isApplyingComments: applyComments.isPending,
    setLinkHref,
    setTextTag,
    duplicateBlock,
    removeBlock,
    moveBlock,
    viewport,
    setViewport,
    refreshStampStatus,
  };
}
